import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Text } from '@viapeople/components'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { PopulationFields } from '../../../../../components/PopulationFields'
import { useToast } from '../../../../../utils/ToastContext'
import { trpc } from '../../../../../utils/trpc'
import type { GetCycleUnsolicitedReviewer } from '../types'

export const ManageUnsolicitedReviewers = () => {
	const { id, organizationId, cycleId } = useParams<{
		id: string
		organizationId: string
		cycleId: string
	}>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const toast = useToast()
	const { data, isLoading: isLoadingQuery } = useQuery({
		enabled: !!id,
		...trpc.admin.cycles.reviewers.unsolicited.show.queryOptions({
			id: id as string,
		}),
	})

	const buildDefaultValues = (data?: GetCycleUnsolicitedReviewer) => {
		return {
			reviewersPopulation: {
				roles:
					data?.reviewersPopulation?.roles.map((role) => ({
						id: role.id,
						label: role.displayName,
					})) || [],
				whitelistedUsers:
					data?.reviewersPopulation?.whitelistedUsers.map((user) => ({
						id: user.id,
						externalId: user.externalId,
						label: `${user.firstName} ${user.lastName}`,
					})) || [],
				blacklistedUsers:
					data?.reviewersPopulation?.blacklistedUsers.map((user) => ({
						id: user.id,
						externalId: user.externalId,
						label: `${user.firstName} ${user.lastName}`,
					})) || [],
				blacklistedHierarchyRoles: [],
				hierarchyLevels: [],
			},
			targetPopulation: {
				roles:
					data?.targetPopulation?.roles.map((role) => ({
						id: role.id,
						label: role.displayName,
					})) || [],
				whitelistedUsers:
					data?.targetPopulation?.whitelistedUsers.map((user) => ({
						id: user.id,
						externalId: user.externalId,
						label: `${user.firstName} ${user.lastName}`,
					})) || [],
				blacklistedUsers:
					data?.targetPopulation?.blacklistedUsers.map((user) => ({
						id: user.id,
						externalId: user.externalId,
						label: `${user.firstName} ${user.lastName}`,
					})) || [],
				blacklistedHierarchyRoles:
					data?.targetPopulation?.blacklistedHierarchyRoles.map((role) => ({
						id: role.id,
						label: role.displayName,
					})) || [],
				hierarchyLevels: data?.targetPopulation?.hierarchyLevels || [],
			},
		}
	}

	const form = useForm({
		defaultValues: buildDefaultValues(data),
	})

	// biome-ignore lint/correctness/useExhaustiveDependencies: buildDefaultValues is intentionally recreated and only data change should trigger reset
	useEffect(() => {
		if (data) {
			form.reset(buildDefaultValues(data))
		}
	}, [data]) // eslint-disable-line react-hooks/exhaustive-deps

	const createUnsolicitedReviewersMutation = useMutation({
		...trpc.admin.cycles.reviewers.unsolicited.create.mutationOptions({
			onSuccess: () => {
				toast.showToast('Unsolicited reviewers created')
				navigate(-1)
			},
			onError: () => {
				toast.showToast('Error creating unsolicited reviewers', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.unsolicited.search.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const editUnsolicitedReviewersMutation = useMutation({
		...trpc.admin.cycles.reviewers.unsolicited.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Unsolicited reviewers edited')
				navigate(-1)
			},
			onError: () => {
				toast.showToast('Error creating unsolicited reviewers', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.unsolicited.search.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const onSubmit = form.handleSubmit((formData) => {
		const dataToSend = {
			id: data?.id,
			cycleId: cycleId as string,
			reviewersPopulation: {
				roles: formData.reviewersPopulation.roles.map((role: { id: string }) => role.id),
				whitelistedUsers: formData.reviewersPopulation.whitelistedUsers.map((user: { id: string }) => user.id),
				blacklistedUsers: formData.reviewersPopulation.blacklistedUsers.map((user: { id: string }) => user.id),
				blacklistedHierarchyRoles: formData.reviewersPopulation.blacklistedHierarchyRoles.map(
					(role: { id: string }) => role.id,
				),
				hierarchyLevels: formData.reviewersPopulation.hierarchyLevels,
			},
			targetPopulation: {
				roles: formData.targetPopulation.roles.map((role: { id: string }) => role.id),
				whitelistedUsers: formData.targetPopulation.whitelistedUsers.map((user: { id: string }) => user.id),
				blacklistedUsers: formData.targetPopulation.blacklistedUsers.map((user: { id: string }) => user.id),
				blacklistedHierarchyRoles: formData.targetPopulation.blacklistedHierarchyRoles.map(
					(role: { id: string }) => role.id,
				),
				hierarchyLevels: formData.targetPopulation.hierarchyLevels,
			},
		}

		if (id) {
			editUnsolicitedReviewersMutation.mutate({
				...dataToSend,
				id,
			})
		} else {
			createUnsolicitedReviewersMutation.mutate({
				...dataToSend,
				cycleId: cycleId as string,
			})
		}
	})

	const loading =
		createUnsolicitedReviewersMutation.isPending || editUnsolicitedReviewersMutation.isPending || isLoadingQuery

	return (
		<Modal
			isOpen
			title={id ? 'Edit Unsolicited' : 'Add Unsolicited'}
			fullContent
			fullScreen
			onClose={() => navigate(-1)}
			bodyClassName='max-h-[90vh] overflow-y-auto'>
			<FormProvider {...form}>
				<form onSubmit={onSubmit} className='relative p-6 pb-12 w-full'>
					<Text variant='medium' className='block mb-2 text-left bg-neutral-100 p-4 rounded-md'>
						Allow the following roles:
					</Text>
					<PopulationFields
						name='reviewersPopulation'
						cycleId={cycleId as string}
						organizationId={organizationId as string}
						projection={{
							title: false,
							hierarchyTitle: false,
							hierarchyLevels: false,
							blacklistedHierarchyRoles: false,
						}}
					/>

					<Text variant='medium' className='block mb-2 text-left bg-neutral-100 p-4 rounded-md mt-8'>
						To unsolicitedly review the following population:
					</Text>
					<PopulationFields
						name='targetPopulation'
						cycleId={cycleId as string}
						organizationId={organizationId as string}
						projection={{
							title: false,
						}}
					/>

					<div className='flex flex-row justify-end gap-4 mt-8'>
						<Button
							variant='secondary'
							onClick={(e) => {
								e.preventDefault()
								navigate(-1)
							}}>
							Cancel
						</Button>
						<Button type='submit' disabled={loading} isLoading={loading}>
							{data ? 'Save Changes' : 'Add Unsolicited Reviewers'}
						</Button>
					</div>
				</form>
			</FormProvider>
		</Modal>
	)
}
